/**
 * SmartTaskGenerator — taken voorstellen op basis van de OFFERTE (lokale
 * offerteregels via de AI-engine) i.p.v. een vast type-template. AI maakt een
 * korte lijst met bundels (hoofdtaak
 * "Casting"/"Locatie" met subtaken); de PL redigeert alles in de preview en maakt
 * dan pas aan. Taken komen zonder datum in Airtable (NogInTePlannen) — de planner
 * plant ze in.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack,
  TextField, MenuItem, IconButton, Chip, CircularProgress, Alert, Box, Tooltip, Divider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { startActivity } from '../../utils/activityTracker';

const TYPES = ['Editor', 'Animator', 'Illustrator', 'Projectleider', 'Copywriter', 'Regisseur', 'Cameraman', 'Creative', 'Ondertitelen', 'Productie-assistent', 'Algemeen'];

export default function SmartTaskGenerator({ open, onClose, projectId, projectName, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [lines, setLines] = useState([]);
  const [showLines, setShowLines] = useState(false);
  const [subInput, setSubInput] = useState({}); // taak-index → invoerveld
  const [done, setDone] = useState(null);       // { created, subtasksCreated, taskIds, subtaskIds }
  const [undoing, setUndoing] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [remembered, setRemembered] = useState(false); // laatste chat-instructie onthouden?
  const undoKey = `smarttasks-undo-${projectId}`;
  const [prevBatch, setPrevBatch] = useState(null); // eerder aangemaakte batch (localStorage)

  const suggest = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError(null); setTasks([]);
    const act = startActivity('Taken voorstellen uit offerte', { path: window.location.pathname });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/suggest-from-offer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Voorstel maken mislukt');
      setTasks(d.tasks || []);
      setLines(d.lines || []);
      act.done(`${(d.tasks || []).length} taken voorgesteld`);
    } catch (e) { setError(e.message); act.fail(e.message); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    setDone(null);
    try { setPrevBatch(JSON.parse(localStorage.getItem(undoKey) || 'null')); } catch { setPrevBatch(null); }
    suggest();
  }, [open, suggest, undoKey]);

  const undoBatch = async (batch) => {
    setUndoing(true); setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/undo-proposal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, taskIds: batch.taskIds || [], subtaskIds: batch.subtaskIds || [] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Terugdraaien mislukt');
      try { localStorage.removeItem(undoKey); } catch { /* quota */ }
      setPrevBatch(null); setDone(null);
      onCreated?.(); // takenlijst verversen
    } catch (e) { setError(e.message); }
    finally { setUndoing(false); }
  };

  const patchTask = (i, patch) => setTasks(prev => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const removeTask = (i) => setTasks(prev => prev.filter((_t, idx) => idx !== i));
  const addTask = () => setTasks(prev => [...prev, { naam: '', type: 'Projectleider', uren: 2, subtaken: [], bron: 'handmatig' }]);
  const addSub = (i) => {
    const v = (subInput[i] || '').trim();
    if (!v) return;
    patchTask(i, { subtaken: [...(tasks[i].subtaken || []), v] });
    setSubInput(s => ({ ...s, [i]: '' }));
  };

  // 💬 Chat over het voorstel — AI werkt de lijst bij en legt kort uit wat er veranderde.
  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const messages = [...chatMsgs, { role: 'user', content: text }];
    setChatMsgs(messages); setChatInput(''); setChatBusy(true); setRemembered(false);
    const act = startActivity('Takenvoorstel aanpassen', { path: window.location.pathname });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/suggest-chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, tasks, lines, projectName }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Chat mislukt');
      setTasks(d.tasks || tasks);
      setChatMsgs(prev => [...prev, { role: 'assistant', content: d.reply || 'Aangepast ✔' }]);
      act.done('Voorstel bijgewerkt');
    } catch (e) {
      setChatMsgs(prev => [...prev, { role: 'assistant', content: `⚠️ ${e.message}` }]);
      act.fail(e.message);
    } finally { setChatBusy(false); }
  };

  // 🧠 De laatste chat-instructie onthouden als org-brede regel (self-learning).
  const rememberLast = async () => {
    const lastUser = [...chatMsgs].reverse().find(m => m.role === 'user');
    const lesson = (lastUser?.content || '').trim();
    if (!lesson) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/learn`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lesson }),
      });
      if (r.ok) setRemembered(true);
    } catch { /* niet kritiek */ }
  };

  const create = async () => {
    const valid = tasks.filter(t => (t.naam || '').trim());
    if (!valid.length) { setError('Geen taken om aan te maken.'); return; }
    setCreating(true); setError(null);
    const act = startActivity(`${valid.length} taken aanmaken`, { path: window.location.pathname });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/create-from-proposal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, tasks: valid }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Aanmaken mislukt');
      act.done(`${d.created} taken + ${d.subtasksCreated} subtaken aangemaakt`);
      // Batch bewaren zodat 'ie in één klik terug te draaien is — óók na sluiten.
      try { localStorage.setItem(undoKey, JSON.stringify({ taskIds: d.taskIds || [], subtaskIds: d.subtaskIds || [], at: Date.now() })); } catch { /* quota */ }
      setDone(d);
      onCreated?.(d);
    } catch (e) { setError(e.message); act.fail(e.message); }
    finally { setCreating(false); }
  };

  const totalHours = tasks.reduce((s, t) => s + (Number(t.uren) || 0), 0);

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon sx={{ color: '#7c3aed' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="div">Slimme taken uit de offerte</Typography>
          <Typography variant="caption" color="text.secondary">{projectName}</Typography>
        </Box>
        {!loading && tasks.length > 0 && (
          <Chip size="small" label={`${tasks.length} taken · ~${totalHours}u`} sx={{ fontWeight: 700 }} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {done ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <Alert severity="success" sx={{ width: '100%' }}>
              <b>{done.created} taken</b> en <b>{done.subtasksCreated} subtaken</b> aangemaakt — ze staan als
              "nog in te plannen" bij het project. Niet goed? Draai de hele batch in één klik terug.
            </Alert>
            <Button color="error" variant="outlined" disabled={undoing}
              startIcon={undoing ? <CircularProgress size={15} /> : <DeleteOutlineIcon />}
              onClick={() => undoBatch(done)} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {undoing ? 'Terugdraaien…' : 'Ongedaan maken (verwijder deze batch)'}
            </Button>
          </Stack>
        ) : loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Offerteregels lezen en een takenlijst voorstellen…
            </Typography>
          </Stack>
        ) : (
          <>
            {prevBatch?.taskIds?.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}
                action={
                  <Button size="small" color="inherit" disabled={undoing} onClick={() => undoBatch(prevBatch)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {undoing ? 'Bezig…' : 'Terugdraaien'}
                  </Button>
                }>
                Er staat nog een eerdere batch van {prevBatch.taskIds.length} slimme taken voor dit project — die kun je hier terugdraaien.
              </Alert>
            )}
            {lines.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button size="small" onClick={() => setShowLines(v => !v)}
                  endIcon={showLines ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ textTransform: 'none' }}>
                  {lines.length} offerteregels als bron
                </Button>
                {showLines && (
                  <Box sx={{ mt: 0.5, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5, maxHeight: 180, overflowY: 'auto' }}>
                    {lines.map((l, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                        {l.sectie ? `[${l.sectie}] ` : ''}{l.aantal}× {l.omschrijving}{l.eenheid ? ` (${l.eenheid})` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Redigeer het voorstel: pas namen/uren aan, verwijder wat niet nodig is, en beheer de subtaken per bundel.
              Taken worden zonder datum aangemaakt ("nog in te plannen") — de planner plant ze in.
            </Typography>
            <Stack spacing={1}>
              {tasks.map((t, i) => (
                <Box key={i} sx={{ p: 1.25, border: '1px solid #e5e7eb', borderRadius: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" fullWidth value={t.naam} placeholder="Taaknaam"
                      onChange={(e) => patchTask(i, { naam: e.target.value })} />
                    <TextField size="small" select value={t.type} sx={{ width: 170 }}
                      onChange={(e) => patchTask(i, { type: e.target.value })}>
                      {TYPES.map(ty => <MenuItem key={ty} value={ty}>{ty}</MenuItem>)}
                    </TextField>
                    <TextField size="small" type="number" value={t.uren} sx={{ width: 80 }}
                      inputProps={{ min: 0 }} label="uur" onChange={(e) => patchTask(i, { uren: e.target.value })} />
                    <IconButton size="small" onClick={() => removeTask(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Stack>
                  {t.bron && <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>bron: {t.bron}</Typography>}
                  {t.suggestion && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: t.suggestion.matched ? '#0f766e' : '#64748b' }}>
                      💡 Voorstel: {t.suggestion.firstName || t.suggestion.name}{t.suggestion.matched ? '' : ' (geen exacte rol-match)'} — {t.suggestion.availableHours}u vrij, {t.suggestion.capacityPercentage}% belast (kom. 3 wkn)
                    </Typography>
                  )}
                  {t.warning && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#b45309', fontWeight: 600 }}>⚠️ {t.warning}</Typography>
                  )}
                  {/* Subtaken (bundel) */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.75 }}>
                    {(t.subtaken || []).map((s, si) => (
                      <Chip key={si} size="small" label={s}
                        onDelete={() => patchTask(i, { subtaken: t.subtaken.filter((_x, xi) => xi !== si) })} />
                    ))}
                    <TextField size="small" variant="standard" placeholder="+ subtaak (Enter)"
                      value={subInput[i] || ''}
                      onChange={(e) => setSubInput(s => ({ ...s, [i]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSub(i); } }}
                      sx={{ width: 170, '& input': { fontSize: 12.5 } }} />
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={addTask} sx={{ textTransform: 'none', mt: 1 }}>
              Taak toevoegen
            </Button>
            <Divider sx={{ my: 1.5 }} />
            {/* Chat: pas het voorstel aan in gewone taal ("minder taken", "bundel casting", …) */}
            {chatMsgs.length > 0 && (
              <Stack spacing={0.75} sx={{ mb: 1, maxHeight: 180, overflowY: 'auto' }}>
                {chatMsgs.map((m, i) => (
                  <Box key={i} sx={{
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    bgcolor: m.role === 'user' ? '#ede9fe' : '#f1f5f9',
                    px: 1.25, py: 0.75, borderRadius: 1.5, maxWidth: '85%',
                  }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                  </Box>
                ))}
                {chatBusy && <Typography variant="caption" color="text.secondary">Voorstel aanpassen…</Typography>}
                {/* Onthoud de laatste instructie zodat volgende voorstellen er rekening mee houden */}
                {!chatBusy && chatMsgs.some(m => m.role === 'assistant') && (
                  remembered ? (
                    <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700 }}>🧠 Onthouden — geldt voor volgende voorstellen ✔</Typography>
                  ) : (
                    <Button size="small" onClick={rememberLast} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: '#7c3aed' }}>
                      🧠 Onthoud dit voor volgende keer
                    </Button>
                  )
                )}
              </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" fullWidth placeholder='Pas het voorstel aan via chat — bv. "minder taken", "bundel alles rond casting", "draaidag is al gepland"'
                value={chatInput} disabled={chatBusy}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }} />
              <Button variant="outlined" onClick={sendChat} disabled={chatBusy || !chatInput.trim()}
                startIcon={chatBusy ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                Pas aan
              </Button>
              <Tooltip title="Opnieuw voorstellen op basis van de offerte (aanpassingen en chat gaan verloren)">
                <Button size="small" onClick={() => { setChatMsgs([]); suggest(); }} disabled={loading} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                  ↻ Opnieuw
                </Button>
              </Tooltip>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={creating || undoing} sx={{ textTransform: 'none' }}>
          {done ? 'Sluiten' : 'Annuleren'}
        </Button>
        {!done && (
          <Button variant="contained" onClick={create} disabled={creating || loading || !tasks.length}
            startIcon={creating ? <CircularProgress size={15} color="inherit" /> : <AutoAwesomeIcon />}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {creating ? 'Aanmaken…' : `Maak ${tasks.filter(t => (t.naam || '').trim()).length} taken aan`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
